import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useSwipeToDismiss } from '@/shared/ui/useSwipeToDismiss'

/**
 * The phone's menu: every first-level destination on one screen (RB-40, D84).
 *
 * It replaced a 224 px drawer that held 20 rows of 48 px in one column, so Finance, Team,
 * Settings and Help were always below a scroll cue. The rows themselves are `NavLinks` in its
 * `grid` variant, passed as `children` — the same table, role filter and badges as the desktop
 * sidebar, two columns of 44 px rows.
 *
 * **Non-modal, on purpose.** Andras's rule is that re-tapping the control that opened the menu
 * closes it and shows the page as it was. That control lives in the bar BELOW the menu, and a
 * modal Radix dialog blocks pointer events outside itself — so the menu is a panel that stops
 * above the bar (`bottomOffset`), takes focus when it opens (its close button), hands focus back
 * to the opener when it closes, and closes on Escape, on its X, on a swipe down (the bottom
 * sheets' own `useSwipeToDismiss`) and — via the host's `useOverlayRouteReset` — on route change.
 * It never navigates by itself, so "the page as it was" is simply still mounted underneath.
 */
export function FullScreenMenu({
  open, onClose, title, header, children, topOffset = '0px', bottomOffset = '0px', returnFocusTo,
}: {
  open: boolean
  onClose: () => void
  /** The accessible name of the panel. */
  title: string
  /** The header row's left side: studio name, storage pill, theme toggle, account. */
  header: React.ReactNode
  children: React.ReactNode
  topOffset?: string
  bottomOffset?: string
  /** The control that opened the menu; focus returns to it on close. */
  returnFocusTo?: React.RefObject<HTMLElement | null>
}) {
  const { t } = useTranslation()
  const closeRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(false)

  const { onTouchStart, onTouchMove, onTouchEnd, transform, transition, attachNode } =
    useSwipeToDismiss('bottom', onClose, false, undefined)

  // Focus in on open, back to the opener on close (not on first mount).
  useEffect(() => {
    if (open) {
      closeRef.current?.focus({ preventScroll: true })
    } else if (wasOpen.current) {
      returnFocusTo?.current?.focus({ preventScroll: true })
    }
    wasOpen.current = open
  }, [open, returnFocusTo])

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose() }
  }, [onClose])

  if (!open) return null
  return (
    <div
      ref={attachNode}
      role="dialog"
      aria-modal="false"
      aria-label={title}
      data-state="open"
      data-testid="phone-menu"
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      // IN-44: a layer that starts at the top edge insets itself — it inherits nothing from
      // whatever it covers, and its header row would otherwise sit under the status bar.
      className={`fixed inset-x-0 z-40 flex flex-col bg-background md:hidden ${topOffset === '0px' ? 'pt-safe' : ''}`}
      style={{ top: topOffset, bottom: bottomOffset, transform, transition }}
    >
      <div className="flex min-h-14 shrink-0 items-center gap-1 border-b px-4">
        <div className="flex min-w-0 flex-1 items-center gap-1">{header}</div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label={t('layout:mobileNav.closeMenu')}
          className="-mr-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md active:bg-accent [-webkit-tap-highlight-color:transparent]"
        >
          <X size={20} />
        </button>
      </div>
      {children}
    </div>
  )
}
